import React, {useContext, useEffect, useState} from 'react';
import {Alert, Box, Button, CircularProgress, TextField} from '@mui/material';
import {Link as RouterLink, useNavigate} from "react-router-dom";
import {api} from '../api/apiService';
import {AuthContext} from "../auth/AuthContext";
import {AuthPageLayout, authLinkStyle, authPrimaryButtonStyle, authTextFieldSx} from './auth/AuthPageLayout';

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState<{severity: 'success' | 'error'; text: string} | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const validateEmail = (email: string): boolean => /\S+@\S+\.\S+/.test(email);

  const {isAuthenticated} = useContext(AuthContext);
  const navigate = useNavigate();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();

    const emailError = validateEmail(email) ? '' : 'Invalid email address';
    setError(emailError);

    if (emailError) return;

    setIsLoading(true);
    setMessage(null);
    try {
      const base_url = window.location.origin; // Get the base URL
      await api.post('/api/auth/forgot-password', {email, base_url}, {skipAuth: true});
      setMessage({severity: 'success', text: 'If the email is associated with an account, a reset link has been sent.'});
    } catch (error: any) {
      console.error('Forgot password failed:', error);
      setMessage({severity: 'error', text: 'An error occurred while processing your request.'});
    } finally {
      setIsLoading(false);
    }
  };


  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  if (isAuthenticated) {
    return null; // Prevent rendering
  }

  return (
    <AuthPageLayout title="Forgot Password" subtitle="Enter your email address to receive a password reset link.">
      {isLoading ? (
        <CircularProgress sx={{color: 'white', mt: 2}}/>
      ) : (
        <form onSubmit={handleSubmit} style={{width: '100%'}}>
          <TextField
            autoComplete="email"
            autoFocus
            label="Email"
            type="email"
            fullWidth
            margin="normal"
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!!error}
            helperText={error}
            sx={authTextFieldSx}
          />
          {message && <Alert severity={message.severity} sx={{mt: 1}}>{message.text}</Alert>}
          <Button type="submit" variant="contained" fullWidth style={authPrimaryButtonStyle}>
            Send Reset Link
          </Button>
          <Box textAlign="center" mt={2}>
            <RouterLink to="/" style={authLinkStyle}>Back to Login</RouterLink>
          </Box>
        </form>
      )}
    </AuthPageLayout>
  );
};
